import { SpaDaysCategoryVersion } from '@features/spa/pages/SpaDaysCategory/SpaDaysCategoryVersion';
import { Property } from 'shared-types/Property';
import { NormalizedStore } from '@store/entity/entity.types';
import * as journeyActions from '@store/yourJourney/actions';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { NextPage, NextPageContext } from 'next';
import React from 'react';
import { getSpaSettingsPrefetch } from '@features/spa';
import { getAllSpaPackagesPrefetch } from '@features/spa/hooks/useGetAllSpaPackages';
import { getAvailableSpaPackagesPrefetch } from '@features/spa/hooks/useGetAvailableSpaPackages';
import { getSpaCategoriesPrefetch } from '@features/spa/hooks/useGetSpaCategories';
import { QueryClient } from '@tanstack/react-query';
import { parseDateToString } from '@utils/helpers';
import * as entityActions from '@store/entity/actions';
import { entitySelectors } from '@store/entity/selectors';
import { Category } from '@features/categories/types';
import { UNCATEGORIZED_VALUE } from '@features/spa/utils/buildCategoryOptions';

type SpaDaysCategoryProps = {
  property: Property;
  language: string;
  category: Category;
};

const SpaDaysCategoryPage: NextPage<SpaDaysCategoryProps> = ({ property, language, category }) => (
  <div>
    <SpaDaysCategoryVersion property={property} language={language} category={category} />
  </div>
);

type SpaDaysCategoryInitialProps = {
  language: string;
  property: Property;
  store: NormalizedStore;
} & NextPageContext;

SpaDaysCategoryPage.getInitialProps = async ({
  store,
  property,
  language,
  query,
}: SpaDaysCategoryInitialProps) => {
  const isUncategorized = query.id === UNCATEGORIZED_VALUE;

  if (property) {
    const { fromDate } = yourJourneySelectors.getParsedJourneyParams(store.getState());
    const spaDate = yourJourneySelectors.getActualSpaDateValue(store.getState());
    const spaDateToSet = spaDate || fromDate;

    const client = new QueryClient();
    await Promise.all([
      ...(isUncategorized
        ? []
        : [entityActions.fetchCategory(property.id, query.id, language)(store.dispatch)]),
      getAllSpaPackagesPrefetch(client, property.id, language),
      getAvailableSpaPackagesPrefetch(
        client,
        property.id,
        { category: null, date: parseDateToString(spaDateToSet) },
        language
      ),
      getSpaCategoriesPrefetch(client, property.id, 'packages'),
      getSpaSettingsPrefetch(client, property.id),
      store.dispatch(journeyActions.changeJourneyParam('spaDate', spaDateToSet)),
    ]);
  }

  const category = isUncategorized
    ? ({ id: UNCATEGORIZED_VALUE, name: '', description: null, image: null } as unknown as Category)
    : entitySelectors.getCategory(store.getState(), query.id);

  return {
    property,
    language,
    category,
  };
};

export default SpaDaysCategoryPage;
